// src/components/workspace/shell/WorkspaceShell.js

import { X } from "lucide-react";

/**
 * Full-viewport three-zone layout for the incident console.
 *
 * The page itself never scrolls; each zone scrolls independently, so the command
 * bar and the session stay put while you read the timeline. On narrow screens the
 * two rails become full-screen sheets.
 *
 * Rails are hidden with CSS rather than unmounted. This is deliberate: the comms
 * rail can hold a live WebRTC session, and collapsing the rail must not drop the
 * call.
 */

function RailContainer({ open, side, title, onClose, children }) {
  const dockedWidth = side === "left" ? "lg:w-80" : "lg:w-96";
  const dockedBorder = side === "left" ? "lg:border-r" : "lg:border-l";

  return (
    <aside
      className={`${open ? "flex" : "hidden"} fixed inset-0 z-40 flex-col bg-white lg:static lg:z-auto lg:inset-auto lg:h-full lg:flex-shrink-0 ${dockedWidth} ${dockedBorder} lg:border-slate-200`}
      aria-label={title}
    >
      {/* Sheet header, mobile only. */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 lg:hidden">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={`Close ${title}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </aside>
  );
}

export default function WorkspaceShell({
  commandBar,
  contextRail,
  main,
  commsRail,
  contextOpen,
  commsOpen,
  onCloseContext,
  onCloseComms,
  density,
}) {
  return (
    <div
      data-density={density}
      className="flex h-screen flex-col overflow-hidden bg-slate-50"
    >
      {commandBar}

      <div className="flex min-h-0 flex-1">
        <RailContainer
          open={contextOpen}
          side="left"
          title="Incident context"
          onClose={onCloseContext}
        >
          {contextRail}
        </RailContainer>

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white lg:border-r lg:border-slate-200">
          {main}
        </main>

        <RailContainer
          open={commsOpen}
          side="right"
          title="Communications"
          onClose={onCloseComms}
        >
          {commsRail}
        </RailContainer>
      </div>
    </div>
  );
}
